import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { CheckedImportMenu, CheckedImportMenuDetail } from 'features/Store/models';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { SUFFIX_REARRANGE_MENU, displayError, handleServiceError } from 'utils';
import AlertModal from '../AlertModal';
import FeatureTitleBar from '../FeatureTitleBar';
import useGlobalModal from '../GlobalModal/useGlobalModal';
import './ReArrangeMenuModal.scss';
import reArrangeMenuService from './reArrangeMenuService';

const COUPON_CLASSIFICATION_KEY: { [key: number]: string } = {
  0: 'U',
  1: 'C',
  2: 'T',
};

export interface ReArrangeMenuModalProps {
  menu: CheckedImportMenu;
  onClose?: (isDataChanged: boolean) => void;
}

function ReArrangeMenuModal({ menu, onClose }: ReArrangeMenuModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  // const menu = MOCK_MENU_DATA;

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const menuNameRef = useRef<HTMLInputElement>(null);

  // state
  const [targetMenuDetails, setTargetMenuDetails] = useState<CheckedImportMenuDetail[]>(menu.menu_menu_details);
  const [reArrangeMenuDetail, setReArrangeMenuDetail] = useState<CheckedImportMenuDetail>();
  const [nextReArrangeMenuIndex, setNextReArrangeMenuIndex] = useState<number>();

  // values
  const isSubMenu = menu.menu_menu_details.some(function (menuDetail) {
    return menuDetail.pos_code.sub_flg === true;
  });

  useEffect(() => {
    fetchNextReArrangeMenuIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchNextReArrangeMenuIndex() {
    (async () => {
      try {
        const response = await reArrangeMenuService.getNextReArrangeMenuIndex();
        setNextReArrangeMenuIndex(Number(response.data));
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleClose(isDataChanged: boolean = false) {
    onClose?.(isDataChanged);
  }

  function handleSelectedTargetMenuChange(event: ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    if (checked) {
      const selectedTargetMenuElements = document.querySelectorAll<HTMLInputElement>(
        '.selected-target-menu input:checked'
      );
      if (selectedTargetMenuElements.length > 0) {
        selectedTargetMenuElements.forEach(function (selectedTargetMenuElement) {
          selectedTargetMenuElement.checked = false;
        });
        event.target.checked = true;
      }
    }
  }

  function getIncludePrice(menuDetail: CheckedImportMenuDetail) {
    let includeTaxPrice = 0;
    if (menuDetail.pos_code.sub_flg) {
      includeTaxPrice = menuDetail.pos_code.composition_price;
      if (menuDetail.pos_code.composition_price_flg === 2) {
        includeTaxPrice = menuDetail.pos_code.composition_price * -1;
      }
    } else {
      includeTaxPrice = menuDetail.pos_code.main_price;
      if (menuDetail.pos_code.main_price_flg === 2) {
        includeTaxPrice = menuDetail.pos_code.main_price * -1;
      }
    }

    return includeTaxPrice;
  }

  function handleMoveTargetMenu() {
    const selectedTargetMenuElement = document.querySelector<HTMLInputElement>('.selected-target-menu input:checked');
    if (selectedTargetMenuElement) {
      const selectedIndex = Number(selectedTargetMenuElement.value);
      const targetMenuDetail = targetMenuDetails[selectedIndex];
      setReArrangeMenuDetail(targetMenuDetail);

      targetMenuDetails.splice(selectedIndex, 1);
      setTargetMenuDetails(targetMenuDetails);
    }
  }

  function handleReturnTargetMenu() {
    if (reArrangeMenuDetail) {
      targetMenuDetails.unshift(reArrangeMenuDetail);
      setTargetMenuDetails(targetMenuDetails);

      setReArrangeMenuDetail(undefined);
    }
  }

  function handleResetFormAfterSubmit() {
    setReArrangeMenuDetail(undefined);
    if (menuNameRef.current) menuNameRef.current.value = '';

    if (targetMenuDetails.length > 1) {
      fetchNextReArrangeMenuIndex();
    } else {
      // re-arrange menu action is over --> close modal
      handleClose(true);
    }
  }

  function handleSubmit() {
    if (menuNameRef.current && nextReArrangeMenuIndex && reArrangeMenuDetail) {
      const menuName = menuNameRef.current.value.trim();
      if (menuName.length === 0) {
        showGlobalModal(() => (
          <AlertModal
            title={t('App.warning')}
            message={t('ReArrangeMenuModal.warning_menu_name_required')}
            onClose={() => {
              setTimeout(() => {
                if (menuNameRef.current) {
                  menuNameRef.current.value = '';
                  menuNameRef.current.focus();
                }
              }, 100);
            }}
          />
        ));
      } else {
        const payload = {
          menu_name: menuNameRef.current.value.trim(),
          menu_detail_id: reArrangeMenuDetail.id,
          rearrange_index: nextReArrangeMenuIndex,
        };

        (async () => {
          try {
            const response = await reArrangeMenuService.add(payload);
            showGlobalModal(() => (
              <AlertModal
                title={t('App.info')}
                message={response.message}
                onClose={() => {
                  //reset form or close modal
                  handleResetFormAfterSubmit();
                }}
              />
            ));
          } catch (e) {
            if (!handleServiceError(showGlobalModal, e as AxiosError)) {
              displayError(showGlobalModal, e as AxiosError, () => {
                fetchNextReArrangeMenuIndex();
              });
            }
          }
        })();
      }
    }
  }

  function getShortNameCouponClassification(couponClassification: number) {
    return COUPON_CLASSIFICATION_KEY[couponClassification] ?? '';
  }
  const couponClassificationName = getShortNameCouponClassification(menu.coupon_classification);

  return (
    <Modal
      size="lg"
      dialogClassName="re-arrange-menu-modal"
      className="alert-modal"
      show
      backdrop="static"
      centered
      onHide={handleClose}
    >
      <Modal.Header
        closeButton
        className={`bg-primary1 justify-content-center ${isEditMode ? 'reedit-mode-wrapper' : ''}`}
      >
        <FeatureTitleBar title={t('ReArrangeMenuModal.head_title')} />
      </Modal.Header>

      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <div className="text-end mb-2">
          <Button className="btn--dark-green" onClick={handleSubmit}>
            {t('ReArrangeMenuModal.save')}
          </Button>
        </div>
        <Row>
          <Col>
            <div className="mb-2">
              <strong>{t('ReArrangeMenuModal.target_menu')}</strong>
            </div>
            <div className="menu-col-wrapper">
              <div className="mb-1">〇{t('ReArrangeMenuModal.menu')}</div>
              <div className="w-75 mb-5">
                <Table bordered className="table-menu-info">
                  <tbody>
                    <tr>
                      <th>{t('ReArrangeMenuModal.menu_code')}</th>
                      <td className="text-center">{menu.food_system_menu.cd + couponClassificationName}</td>
                    </tr>
                    <tr>
                      <th>{t('ReArrangeMenuModal.menu_name')}</th>
                      <td className="text-center">
                        <div style={{ lineHeight: '29px' }}>{menu.food_system_menu.name}</div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('ReArrangeMenuModal.menu_type')}</th>
                      <td className="text-center">
                        {isSubMenu ? t('ReArrangeMenuModal.sub_menu') : t('ReArrangeMenuModal.main_menu')}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="mb-2">
                <Row className="align-items-center">
                  <Col>
                    <div className="mb-1">〇{t('ReArrangeMenuModal.pos_info')}</div>
                  </Col>
                  <Col className="text-end">
                    <Button className="btn-move btn-move--right" onClick={handleMoveTargetMenu}>
                      {t('ReArrangeMenuModal.move')}
                    </Button>
                  </Col>
                </Row>
              </div>
              <div>
                <div className="sticky-table">
                  <Table bordered>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>{t('ReArrangeMenuModal.selection')}</th>
                        <th style={{ width: '24%' }}>{t('ReArrangeMenuModal.pos_cd')}</th>
                        <th style={{ width: '30%' }}>{t('ReArrangeMenuModal.pos_name')}</th>
                        <th style={{ width: '11%' }}>{t('ReArrangeMenuModal.pos_type')}</th>
                        <th
                          style={{ width: '25%' }}
                          dangerouslySetInnerHTML={{ __html: t('ReArrangeMenuModal.price_not_include_tax') }}
                        ></th>
                        <th
                          style={{ width: '25%' }}
                          dangerouslySetInnerHTML={{ __html: t('ReArrangeMenuModal.price_include_tax') }}
                        ></th>
                      </tr>
                    </thead>
                    <tbody>
                      {targetMenuDetails.map((targetMenuDetail, targetMenuDetailIndex) => {
                        const includeTaxPrice = getIncludePrice(targetMenuDetail);
                        return (
                          <tr key={targetMenuDetail.id}>
                            <td className="text-center">
                              {!targetMenuDetail.pos_code.sub_flg && reArrangeMenuDetail === undefined && (
                                <Form.Check
                                  className="selected-target-menu"
                                  value={targetMenuDetailIndex}
                                  onChange={handleSelectedTargetMenuChange}
                                ></Form.Check>
                              )}
                            </td>
                            <td className="text-center">{targetMenuDetail.pos_code.cd}</td>
                            <td className="text-center">{targetMenuDetail.name}</td>
                            <td className="text-center">
                              {targetMenuDetail.pos_code.sub_flg
                                ? t('ReArrangeMenuModal.sub_menu')
                                : t('ReArrangeMenuModal.main_menu')}
                            </td>
                            <td className="text-center">
                              <NumericFormat
                                value={targetMenuDetail.standard_price}
                                displayType={'text'}
                                thousandSeparator={true}
                              />
                            </td>
                            <td className="text-center">
                              <NumericFormat value={includeTaxPrice} displayType={'text'} thousandSeparator={true} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </Col>

          {/* ReArrange Menu */}
          <Col className="h-100">
            <div className="mb-2">
              <strong>{t('ReArrangeMenuModal.target_menu')}</strong>
            </div>
            <div className="menu-col-wrapper">
              <div className="mb-1">〇{t('ReArrangeMenuModal.menu')}</div>
              <div className="w-75 mb-5">
                <Table bordered className="table-menu-info">
                  <tbody>
                    <tr>
                      <th>{t('ReArrangeMenuModal.menu_code')}</th>
                      <td className="text-center">
                        {menu.food_system_menu.cd}
                        {SUFFIX_REARRANGE_MENU}
                        {nextReArrangeMenuIndex}
                        {couponClassificationName}
                      </td>
                    </tr>
                    <tr>
                      <th>{t('ReArrangeMenuModal.menu_name')}</th>
                      <td className="text-center">
                        <Form.Control ref={menuNameRef} className="text-center"></Form.Control>
                      </td>
                    </tr>
                    <tr>
                      <th>{t('ReArrangeMenuModal.menu_type')}</th>
                      <td className="text-center">{t('ReArrangeMenuModal.main_menu')}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="mb-2">
                <Row className="align-items-center">
                  <Col>
                    <div className="mb-1">〇{t('ReArrangeMenuModal.pos_info')}</div>
                  </Col>
                  <Col className="text-end">
                    <Button className="btn-move btn-move--left" onClick={handleReturnTargetMenu}>
                      {t('ReArrangeMenuModal.return')}
                    </Button>
                  </Col>
                </Row>
              </div>
              <div>
                <div className="sticky-table">
                  <Table bordered>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>{t('ReArrangeMenuModal.selection')}</th>
                        <th style={{ width: '24%' }}>{t('ReArrangeMenuModal.pos_cd')}</th>
                        <th style={{ width: '30%' }}>{t('ReArrangeMenuModal.pos_name')}</th>
                        <th style={{ width: '11%' }}>{t('ReArrangeMenuModal.pos_type')}</th>
                        <th
                          style={{ width: '25%' }}
                          dangerouslySetInnerHTML={{ __html: t('ReArrangeMenuModal.price_not_include_tax') }}
                        ></th>
                        <th
                          style={{ width: '25%' }}
                          dangerouslySetInnerHTML={{ __html: t('ReArrangeMenuModal.price_include_tax') }}
                        ></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reArrangeMenuDetail && (
                        <tr>
                          <td></td>
                          <td className="text-center">{reArrangeMenuDetail.pos_code.cd}</td>
                          <td className="text-center">{reArrangeMenuDetail.name}</td>
                          <td className="text-center">
                            {reArrangeMenuDetail.pos_code.sub_flg
                              ? t('ReArrangeMenuModal.sub_menu')
                              : t('ReArrangeMenuModal.main_menu')}
                          </td>
                          <td className="text-center">
                            <NumericFormat
                              value={reArrangeMenuDetail.standard_price}
                              displayType={'text'}
                              thousandSeparator={true}
                            />
                          </td>
                          <td className="text-center">
                            <NumericFormat
                              value={getIncludePrice(reArrangeMenuDetail)}
                              displayType={'text'}
                              thousandSeparator={true}
                            />
                          </td>
                        </tr>
                      )}
                      {reArrangeMenuDetail === undefined && (
                        <tr>
                          <td className="text-center" colSpan={6}>
                            {t('ReArrangeMenuModal.guide_text')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default ReArrangeMenuModal;
